// CompanyBillingDashboard.js - Company-centric billing dashboard
import React, { useState, useEffect, useMemo } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import { Skeleton } from "primereact/skeleton";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Link, useNavigate } from "react-router-dom";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { makeRequest2 } from "../../../utils/makeRequest";
import { toastify } from "../../../utils/toast";
import { getCompaniesURL } from "../../../utils/urls";
import Layout from "../component/layout";
import { filterObjectsByAllProperties } from "../../../utils/filterSearch";

// Financial Summary Component
const FinancialSummary = ({
  facilityId = null,
  startDate = null,
  endDate = null,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      let url = "/api/core/billing/financial-summary";
      const params = [];
      if (facilityId) params.push(`facilityId=${facilityId}`);
      if (startDate) params.push(`startDate=${startDate}`);
      if (endDate) params.push(`endDate=${endDate}`);
      if (params.length > 0) url += "?" + params.join("&");

      const response = await makeRequest2(url, "GET");

      console.log("[FinancialSummary] API Response:", response);
      if (response.success) {
        console.log(
          "[FinancialSummary] Data loaded successfully:",
          response.data?.summary || response.summary,
        );
        // Handle nested response structure
        const actualData = response.data || response;
        setData(actualData);
        setError(null);
      } else {
        const errorMsg = response.error || "Failed to load financial summary";
        setError(errorMsg);
        toastify(errorMsg, "error");
      }
    } catch (error) {
      const errorMsg = error.message || "Error loading summary";
      setError(errorMsg);
      toastify(errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    console.log("[FinancialSummary] Component mounted, fetching data...");
    fetchSummary();
  }, [facilityId, startDate, endDate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="col-12 mb-4">
        <Card className="text-center py-5">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <p className="text-muted mt-3">Loading financial summary...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="col-12 mb-4">
        <Card className="border-danger">
          <div className="p-4 text-center">
            <i className="ti ti-alert-circle text-danger fs-1 mb-3"></i>
            <h5 className="text-danger mb-2">
              Failed to Load Financial Summary
            </h5>
            <p className="text-muted mb-3">{error}</p>
            <Button
              label="Retry"
              icon="ti ti-refresh"
              className="p-button-sm"
              onClick={fetchSummary}
            />
          </div>
        </Card>
      </div>
    );
  }

  if (!data || !data.summary) {
    console.log("[FinancialSummary] No data available, not rendering", data);
    return (
      <div className="col-12 mb-4">
        <Card className="border-warning">
          <div className="p-4 text-center">
            <i className="ti ti-info-circle text-warning fs-1 mb-3"></i>
            <h5 className="text-warning mb-2">No Financial Data</h5>
            <p className="text-muted">
              No invoices or payments found yet. Start generating invoices to
              see financial summary.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const { summary, breakdown } = data;
  const collectionRate = parseFloat(summary?.collectionRate || 0);

  console.log("[FinancialSummary] Rendering with data:", {
    expected: summary?.expectedCash,
    paid: summary?.paidCash,
    outstanding: summary?.outstanding,
    collectionRate,
  });

  return (
    <div className="col-12 mb-4">
      <div className="row g-3">
        {/* Expected Cash */}
        <div className="col-md-4">
          <Card className="border-0 shadow-sm h-100">
            <div className="d-flex align-items-center justify-content-between p-3">
              <div>
                <div className="text-muted small mb-1">
                  <i className="ti ti-file-invoice me-1"></i>
                  Expected Cash
                </div>
                <h3 className="mb-0 fw-bold text-primary">
                  {formatCurrency(summary?.expectedCash || 0)}
                </h3>
                <small className="text-muted">
                  {summary?.totalInvoices || 0} invoice(s)
                </small>
              </div>
              <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                <i className="ti ti-receipt text-primary fs-2"></i>
              </div>
            </div>
          </Card>
        </div>

        {/* Paid Cash */}
        <div className="col-md-4">
          <Card className="border-0 shadow-sm h-100">
            <div className="d-flex align-items-center justify-content-between p-3">
              <div>
                <div className="text-muted small mb-1">
                  <i className="ti ti-cash me-1"></i>
                  Paid Cash
                </div>
                <h3 className="mb-0 fw-bold text-success">
                  {formatCurrency(summary?.paidCash || 0)}
                </h3>
                <small className="text-muted">
                  {summary?.totalPayments || 0} payment(s)
                </small>
              </div>
              <div className="bg-success bg-opacity-10 rounded-circle p-3">
                <i className="ti ti-coins text-success fs-2"></i>
              </div>
            </div>
          </Card>
        </div>

        {/* Outstanding */}
        <div className="col-md-4">
          <Card className="border-0 shadow-sm h-100">
            <div className="d-flex align-items-center justify-content-between p-3">
              <div>
                <div className="text-muted small mb-1">
                  <i className="ti ti-alert-circle me-1"></i>
                  Outstanding
                </div>
                <h3 className="mb-0 fw-bold text-danger">
                  {formatCurrency(summary?.outstanding || 0)}
                </h3>
                <small className="text-muted">
                  {collectionRate.toFixed(2)}% collected
                </small>
              </div>
              <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                <i className="ti ti-hourglass text-danger fs-2"></i>
              </div>
            </div>
          </Card>
        </div>

        {/* Collection Rate Progress */}
        <div className="col-12">
          <Card className="border-0 shadow-sm">
            <div className="p-3">
              <div className="d-flex justify-content-between mb-2">
                <span className="fw-bold">
                  <i className="ti ti-percentage me-1"></i>
                  Collection Rate
                </span>
                <span className="fw-bold text-primary">
                  {collectionRate.toFixed(2)}%
                </span>
              </div>
              <div className="progress" style={{ height: "20px" }}>
                <div
                  className={`progress-bar ${collectionRate >= 80 ? "bg-success" : collectionRate >= 50 ? "bg-warning" : "bg-danger"}`}
                  role="progressbar"
                  style={{ width: `${collectionRate}%` }}
                  aria-valuenow={collectionRate}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {collectionRate.toFixed(2)}%
                </div>
              </div>
              <small className="text-muted mt-2 d-block">
                {collectionRate >= 80 && "✅ Excellent collection rate"}
                {collectionRate >= 50 &&
                  collectionRate < 80 &&
                  "⚠️ Good, but can improve"}
                {collectionRate < 50 &&
                  "🔴 Needs attention - low collection rate"}
              </small>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const CompanyBillingDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  // Enhanced stats calculation
  const dashboardStats = useMemo(() => {
    const totalCompanies = companies.length;
    const enabledCompanies = companies.filter((c) => c.isEnabled).length;
    const disabledCompanies = companies.filter((c) => !c.isEnabled).length;
    const totalFacilities = companies.reduce(
      (sum, company) => sum + (company.facilities?.length || 0),
      0,
    );

    return {
      totalCompanies,
      enabledCompanies,
      disabledCompanies,
      totalFacilities,
    };
  }, [companies]);

  const getCompanies = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const response = await makeRequest2(getCompaniesURL, "GET", {});
      if (response.success) {
        setCompanies(response.data);
        setFilteredCompanies(response.data);
        toastify("Companies data loaded successfully!", "success");
      }
    } catch (error) {
      toastify(error.message, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleInputSearch = async (value) => {
    try {
      setSearchTerm(value);
      if (value === "") {
        setFilteredCompanies(companies);
      } else {
        const results = await filterObjectsByAllProperties(companies, value);
        setFilteredCompanies(results);
      }
    } catch (error) {
      toastify(error.message, "error");
    }
  };

  const handleViewCompanyBilling = (company) => {
    navigate(`/core/billing/company/${company._id}`, {
      state: { company },
    });
  };

  useEffect(() => {
    getCompanies();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // DataTable Templates
  const facilitiesBodyTemplate = (rowData) => {
    return (
      <Badge
        value={rowData.facilities?.length || 0}
        severity="info"
        className="text-capitalize"
      />
    );
  };

  const statusBodyTemplate = (rowData) => {
    return (
      <Badge
        value={rowData.isEnabled ? "Enabled" : "Disabled"}
        severity={rowData.isEnabled ? "success" : "danger"}
        className="text-capitalize"
      />
    );
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-sm p-button-text p-button-info"
          onClick={() => handleViewCompanyBilling(rowData)}
          tooltip="View Company Billing"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="ti ti-building"
          className="p-button-sm p-button-text p-button-secondary"
          onClick={() =>
            navigate(`/core/companies/view_company/${rowData._id}`)
          }
          tooltip="View Company Details"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  // Enhanced Overview Dashboard
  const renderOverviewDashboard = () => (
    <div className="row g-4">
      {/* Key Metrics Cards */}
      <div className="col-12">
        <div className="row g-3">
          <div className="col-xl-3 col-md-6">
            <div className="card bg-primary text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Total Companies</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="60px" height="32px" />
                      ) : (
                        dashboardStats.totalCompanies.toLocaleString()
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-building fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-success text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Active Companies</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="80px" height="32px" />
                      ) : (
                        dashboardStats.enabledCompanies
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-check fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-warning text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Total Facilities</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="50px" height="32px" />
                      ) : (
                        dashboardStats.totalFacilities
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-building-warehouse fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-3 col-md-6">
            <div className="card bg-danger text-white border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h6 className="text-white-50 mb-2">Inactive Companies</h6>
                    <h3 className="mb-0 text-white">
                      {loading ? (
                        <Skeleton width="40px" height="32px" />
                      ) : (
                        dashboardStats.disabledCompanies
                      )}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="ti ti-ban fs-2 text-white-50"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <FinancialSummary />

      {/* Companies Table */}
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-items-center justify-content-between">
              <h5 className="mb-0">
                <i className="ti ti-building me-2"></i>Companies Overview
              </h5>
              <Button
                icon="ti ti-refresh"
                className="p-button-text p-button-sm"
                onClick={() => getCompanies(false)}
                loading={refreshing}
                tooltip="Refresh Data"
              />
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-5">
                <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                <p className="mt-3 text-muted">Loading companies data...</p>
              </div>
            ) : (
              <DataTable
                value={filteredCompanies}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                emptyMessage="No companies found."
                stripedRows
                showGridlines
                responsiveLayout="scroll"
                sortMode="multiple"
                filterDisplay="menu"
              >
                <Column
                  field="name"
                  header="Company Name"
                  sortable
                  filter
                  filterPlaceholder="Search by name"
                  style={{ minWidth: "200px" }}
                />
                <Column
                  header="Facilities"
                  body={facilitiesBodyTemplate}
                  sortable
                  sortField="facilities.length"
                  style={{ width: "120px", textAlign: "center" }}
                />
                <Column
                  header="Status"
                  body={statusBodyTemplate}
                  sortable
                  sortField="isEnabled"
                  style={{ width: "120px", textAlign: "center" }}
                />
                <Column
                  header="Actions"
                  body={actionsBodyTemplate}
                  style={{ width: "150px", textAlign: "center" }}
                />
              </DataTable>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      {/* Header */}
      <div className="page-header bg-light border-bottom">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-8">
              <ul className="breadcrumb mb-2">
                <li className="breadcrumb-item">
                  <Link to="/core/dashboard" className="text-muted">
                    <i className="ti ti-home me-1"></i>Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active text-dark">
                  Billing Management
                </li>
              </ul>
              <div className="page-header-title">
                <h2 className="mb-1 text-dark fw-semibold">
                  <i className="ti ti-calculator me-2 text-primary"></i>Billing
                  Management
                </h2>
                <p className="mb-0 text-muted">
                  Manage company billing and invoices by organization
                </p>
              </div>
            </div>
            <div className="col-md-4 text-end">
              <div className="d-flex gap-2 justify-content-end">
                <Button
                  icon="ti ti-refresh"
                  className="p-button-outlined p-button-secondary"
                  onClick={() => getCompanies(false)}
                  loading={refreshing}
                  tooltip="Refresh Data"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          {/* Search Bar */}
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row align-items-center g-3">
                <div className="col-md-6">
                  <div className="position-relative">
                    <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                    <InputText
                      value={searchTerm}
                      onChange={(e) => handleInputSearch(e.target.value)}
                      placeholder="Search companies..."
                      className="form-control ps-5"
                      style={{ paddingLeft: "2.5rem" }}
                    />
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <Link
                    to="/core/companies/add_company"
                    className="btn btn-primary"
                  >
                    <i className="ti ti-plus me-2"></i>
                    Add New Company
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="mt-3">{renderOverviewDashboard()}</div>
        </div>
      </div>
    </Layout>
  );
};

export default CompanyBillingDashboard;
